// import Vue from 'vue';
import "@root/scss/custom.scss";
import upperFirst from 'lodash/upperFirst'
import camelCase from 'lodash/camelCase'
import router from '@apexl/icarus-core/src/router/index.js'
import { activeThemeKeys } from '@root/services/themeComponents';
import { createApp } from "vue";
import { createPinia } from "pinia";
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate';
import "bootstrap/dist/js/bootstrap.bundle.js";
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css'
import Icarus from "@apexl/icarus-core/src/components/Icarus.vue";
import { defaultConfig as formkitConfig, plugin as formkit } from "@formkit/vue";

const app = createApp(Icarus);

// Defensive: a 3rd-party script throwing during boot (e.g. remote FA kit
// returning 403, analytics script blocked by an ad blocker, etc.) can
// cascade into Vue's runtime error path and prevent the app mounting.
// This handler logs the error but keeps the app alive. Replace with
// proper Sentry/Bugsnag wiring per project as needed.
app.config.errorHandler = (err, instance, info) => {
  // eslint-disable-next-line no-console
  console.error('[Vue errorHandler]', info, err);
};

const pinia = createPinia();
pinia.use(piniaPluginPersistedstate)
app.use(pinia)
app.use(router);

app.component('VueDatePicker', VueDatePicker);

app.use(formkit, formkitConfig({
  theme: 'genesis'
}));

const requireComponent = require.context(
    '@apexl/icarus-core/src/components', // The relative path of the components' folder
    true, // Whether to look in sub-folders
    /[A-Z]\w+\.(vue|js)$/ // The regular expression used to match base component filenames
);

const requireCustomComponent = require.context(
    './components', // The relative path of the components' folder
    true, // Whether to look in sub-folders
    /[A-Z]\w+\.(vue|js)$/ // The regular expression used to match base component filenames
);

// io-branding B.7 — build-time component theme tier. require.context's
// directory must be static, so this bundles every theme's files; we register
// only the ACTIVE theme's (ICARUS_THEME), which win over project + core.
const requireThemeComponent = require.context(
    './themes',
    true,
    /[A-Z]\w+\.(vue|js)$/
);

const toComponentName = fileName =>
    upperFirst(camelCase(fileName.split('/').pop().replace(/\.\w+$/, '')));

// Precedence: theme > project-custom > core. Each higher tier's names are
// skipped by the lower tiers so Vue never warns "already registered".
const activeTheme = (typeof ICARUS_THEME !== 'undefined' && ICARUS_THEME) ? ICARUS_THEME : '';
const themeKeys   = activeThemeKeys(requireThemeComponent.keys(), activeTheme);
const themeNames  = new Set(themeKeys.map(toComponentName));

const customNames = new Set(
    requireCustomComponent.keys().map(toComponentName)
);

// Theme (highest) — register the active theme's components first.
themeKeys.forEach(fileName => {
    const config = requireThemeComponent(fileName);
    app.component(toComponentName(fileName), config.default || config);
});

importComponents(app, requireCustomComponent, themeNames);                       // project, minus theme-claimed
importComponents(app, requireComponent, new Set([...themeNames, ...customNames])); // core, minus theme + project

function importComponents(app, components, skip = null) {
  components.keys().forEach(fileName => {
    const componentConfig = components(fileName); // Get component config

    // Get PascalCase name of component, Gets the file name regardless of folder depth
    const componentName = toComponentName(fileName);

    if (skip && skip.has(componentName)) {
      return; // a higher tier already claimed this name
    }

    // Register component globally
    // Look for the component options on `.default`, which will
    // exist if the component was exported with `export default`,
    // otherwise fall back to module's root.
    app.component(
        componentName,
        componentConfig.default || componentConfig
    )
  });
}

app.mount('#app');
